import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, Loader2, AlertCircle, MapPin, Calendar, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatChargedAt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function ReceiptView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/receipts/${token}`);
        if (cancelled) return;
        setData(res.data);
        setStatus("ok");
      } catch (err) {
        if (cancelled) return;
        setStatus(err.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/receipts/${token}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Oxford-Cleaners-Receipt-${data?.pickup_id?.slice(0, 8) || "receipt"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Silent — button re-enables. Toast is optional for a public page.
    } finally {
      setDownloading(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="receipt-loading">
        <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (status === "notfound" || status === "error") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div
          className="max-w-md w-full bg-white border border-rose-200 rounded-2xl p-8 text-center shadow-sm"
          data-testid="receipt-error"
        >
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-semibold text-slate-900">
            {status === "notfound" ? "Receipt not found" : "Something went wrong"}
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            {status === "notfound"
              ? "This receipt link may have expired or the receipt was never issued. If you believe this is an error, call us at 480-940-6023."
              : "We couldn't load your receipt right now. Please try again in a moment."}
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center text-sm font-semibold text-brand-blue hover:underline"
            data-testid="receipt-error-home-link"
          >
            Return home →
          </Link>
        </div>
      </div>
    );
  }

  const items = data.services || [];
  const address = [data.address, data.city].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto" data-testid="receipt-view">
        {/* Header */}
        <div className="text-center mb-6">
          <Link
            to="/"
            className="inline-block font-display text-brand-navy text-xl font-semibold tracking-tight hover:opacity-80"
            data-testid="receipt-brand-link"
          >
            Oxford Cleaners
          </Link>
          <p className="text-xs text-slate-500 mt-1">
            5865 W Ray Rd #3, Chandler, AZ · (480) 940-6023
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Amount hero */}
          <div className="bg-brand-navy text-white p-8 text-center relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 px-3 py-1 text-xs font-semibold text-emerald-300">
              <CheckCircle2 className="h-3.5 w-3.5" />
              PAID
            </div>
            <p
              className="mt-4 text-5xl font-display font-bold tracking-tight"
              data-testid="receipt-amount"
            >
              {formatMoney(data.amount_cents)}
            </p>
            {data.card_last4 && (
              <p className="mt-2 text-sm text-slate-300" data-testid="receipt-card">
                Paid with {(data.card_brand || "card").replace(/^\w/, (c) => c.toUpperCase())} •••• {data.card_last4}
              </p>
            )}
            {data.charged_at && (
              <p className="mt-1 text-xs text-slate-400" data-testid="receipt-charged-at">
                {formatChargedAt(data.charged_at)}
              </p>
            )}
          </div>

          {/* Details */}
          <div className="p-8 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">
                Customer
              </p>
              <p className="font-medium text-slate-900" data-testid="receipt-customer-name">
                {data.customer_name}
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Pickup date
                </p>
                <p className="font-medium text-slate-900" data-testid="receipt-pickup-date">
                  {formatDate(data.pickup_date)}
                </p>
                {data.pickup_window && (
                  <p className="text-sm text-slate-500">{data.pickup_window}</p>
                )}
              </div>
              {address && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    Pickup address
                  </p>
                  <p className="font-medium text-slate-900" data-testid="receipt-address">
                    {address}
                  </p>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  Services
                </p>
                <ul
                  className="flex flex-wrap gap-2"
                  data-testid="receipt-services-list"
                >
                  {items.map((s, i) => (
                    <li
                      key={`svc-${i}`}
                      className="text-sm bg-slate-100 text-slate-700 rounded-full px-3 py-1"
                      data-testid={`receipt-service-${i}`}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.payment_intent_id && (
              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-400" data-testid="receipt-payment-intent">
                  Transaction: <code className="text-slate-600">{data.payment_intent_id}</code>
                </p>
              </div>
            )}

            <Button
              onClick={downloadPdf}
              disabled={downloading}
              className="w-full h-12 bg-brand-blue hover:bg-brand-blue/90 gap-2"
              data-testid="receipt-download-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preparing PDF…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Download PDF receipt
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Questions about this receipt? Reply to the receipt email or call{" "}
          <a href="tel:+14809406023" className="text-brand-blue font-semibold hover:underline">
            (480) 940-6023
          </a>
          .
        </p>
      </div>
    </div>
  );
}
